import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flows, isApiError } from '@repletoryreach/sdk-api';
import { Badge, Button, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Select, SegmentedControl, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT, type Translate } from '../../../../i18n/I18nProvider';
import { placeholderProblems, useLanguages } from '../../../../lib/languages';

const MUTED = 'var(--rr-color-text-muted)';
const key = (flowId: string, language: string) => ['flow-translations', flowId, language];

/** The server's refusal message, or the error's own. */
function refusalText(e: unknown, fallback: string): string {
  if (isApiError(e)) return (e.body as { error?: { message?: string } } | null)?.error?.message ?? e.message;
  return e instanceof Error ? e.message : fallback;
}

/** "Message", "Choice 2", "List row 3: description": what a piece of wording is, in words. */
function pathLabel(path: string, t: Translate): string {
  const m = /^(options|buttons|list\.options)\.(\d+)(?:\.(title|label|description))?$/.exec(path);
  if (m) {
    const kind = m[1] === 'list.options' ? 'row' : m[1] === 'buttons' ? 'button' : 'choice';
    return t(`flows.translations.path.${kind}${m[3] === 'description' ? 'Description' : ''}`, { n: Number(m[2]) + 1 });
  }
  return t(`flows.translations.path.${path}`, path);
}

/**
 * The flow builder's Translations tab (1 November 2026). A flow's fixed wording (messages, questions, button and list
 * labels) is translated automatically the first time a customer is answered in another language the chatbot offers;
 * here the business sees each piece beside the original, step by step, corrects it, marks it as read, and translates
 * the missing pieces now. The translations follow the saved flow, so unsaved changes say so.
 */
export function TranslationsPanel({ opened, onClose, flowId, flowName, botId, dirty }: {
  opened: boolean;
  onClose: () => void;
  flowId: string;
  flowName: string;
  botId?: string | null;
  /** The builder has changes not saved yet, which the translations do not have. */
  dirty: boolean;
}) {
  const t = useT();
  const qc = useQueryClient();
  const { languages: catalogue } = useLanguages();
  const [chosen, setChosen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: key(flowId, chosen ?? ''),
    queryFn: () => flows.listFlowTranslations(flowId, chosen ?? undefined),
    enabled: opened,
    retry: false,
    // Another language keeps the list on screen while it loads, rather than flashing empty.
    placeholderData: (prev) => prev
  });
  const data = q.data?.data;
  const language = data?.language ?? null;
  const ownName = (code: string) => catalogue.find((l) => l.code === code)?.native_name ?? code;

  const translate = useMutation({
    mutationFn: (all: boolean) => flows.translateFlow(flowId, { language: language!, all }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['flow-translations', flowId] });
      notifySuccess(t('flows.translations.translated', { count: res.data.written, language: ownName(language!) }), t('flows.translations.titleShort'));
    },
    onError: (e) => notifyError(refusalText(e, t('common.unknownError')), t('flows.translations.translateFailed'))
  });

  // Step by step, in the flow's own order.
  const groups = useMemo(() => {
    const out: Array<{ nodeId: string; name: string; items: flows.FlowTranslationItem[] }> = [];
    for (const item of data?.items ?? []) {
      const last = out[out.length - 1];
      if (last && last.nodeId === item.node_id) last.items.push(item);
      else out.push({ nodeId: item.node_id, name: item.node_name?.trim() || item.node_id, items: [item] });
    }
    return out;
  }, [data]);
  const missing = (data?.items ?? []).filter((i) => i.source === 'missing').length;
  const total = data?.items.length ?? 0;
  const stale = (data?.items ?? []).filter((i) => i.stale).length;

  function confirmAll() {
    if (!language) return;
    openConfirmModal({
      title: t('flows.translations.all.title', { language: ownName(language) }),
      children: <Text role="bodySmall">{t('flows.translations.all.body', { language: ownName(language) })}</Text>,
      labels: { confirm: t('flows.translations.all.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => translate.mutate(true)
    });
  }

  const offered = data?.languages ?? [];
  const settingsLink = botId ? `/settings/assistant/${botId}` : '/settings/assistant';

  return (
    <Drawer opened={opened} onClose={onClose} title={t('flows.translations.title', { name: flowName })} position="right" size="lg">
      <Stack gap="md" data-testid="flow-translations">
        <Text role="bodySmall" c={MUTED}>{t('flows.translations.intro')}</Text>

        {q.isPending ? (
          <Stack gap="sm">{[0, 1, 2].map((i) => <Skeleton key={i} height={96} />)}</Stack>
        ) : q.error ? (
          <Alert color="error">
            <Group justify="space-between" gap="sm" wrap="wrap">
              <Text role="bodySmall">{refusalText(q.error, t('flows.translations.loadError'))}</Text>
              <Button size="xs" variant="outline" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : offered.length === 0 ? (
          <Alert color="info" data-testid="flow-translations-one-language">
            {t('flows.translations.oneLanguage')} <Link to={settingsLink}>{t('flows.translations.openSettings')}</Link>
          </Alert>
        ) : (
          <>
            {offered.length <= 4 ? (
              <SegmentedControl
                value={chosen ?? language ?? offered[0]}
                onChange={(v) => setChosen(v)}
                data={offered.map((code) => ({ value: code, label: ownName(code) }))}
              />
            ) : (
              <Select
                label={t('flows.translations.language')}
                value={chosen ?? language ?? offered[0]}
                onChange={(e) => setChosen(e.currentTarget.value)}
                data={offered.map((code) => ({ value: code, label: ownName(code) }))}
                style={{ maxWidth: 280 }}
              />
            )}
            {dirty && <Alert color="info">{t('flows.translations.unsaved')}</Alert>}
            {data?.quality === 'review' && <Alert color="warning">{t('flows.translations.reviewNote')}</Alert>}

            <Stack gap={4}>
              <Group justify="space-between" align="center" gap="sm" wrap="wrap">
                <Text role="bodySmall" data-testid="flow-translations-count">
                  {total === 0
                    ? t('flows.translations.nothing')
                    : t('flows.translations.count', { done: total - missing, total })}
                  {stale > 0 ? ` · ${t('flows.translations.staleCount', { count: stale })}` : ''}
                </Text>
                <Group gap="xs" wrap="wrap">
                  <Button size="xs" variant="subtle" disabled={translate.isPending || total === missing} onClick={confirmAll}>
                    {t('flows.translations.all.action')}
                  </Button>
                  <Button size="xs" loading={translate.isPending} disabled={missing === 0} onClick={() => translate.mutate(false)} data-testid="flow-translate-now">
                    {t('flows.translations.translateNow')}
                  </Button>
                </Group>
              </Group>
              <Text role="caption" c={MUTED}>{t('flows.translations.charge')}</Text>
            </Stack>

            {groups.map((g) => (
              <Stack key={g.nodeId} gap="xs">
                <Text role="overline" c={MUTED}>{g.name}</Text>
                {g.items.map((item) => (
                  <TranslationRow
                    key={`${item.node_id}:${item.path}`}
                    flowId={flowId}
                    language={language!}
                    languageName={ownName(language!)}
                    item={item}
                  />
                ))}
              </Stack>
            ))}
          </>
        )}
      </Stack>
    </Drawer>
  );
}

/**
 * One piece of wording: the original beside the translation, edited in place, with a character counter for a button,
 * a list row or a heading. "Use a fresh translation" takes the business's own version back; "Mark as read" records
 * that someone has checked it.
 */
function TranslationRow({ flowId, language, languageName, item }: {
  flowId: string;
  language: string;
  languageName: string;
  item: flows.FlowTranslationItem;
}) {
  const t = useT();
  const qc = useQueryClient();
  const [draft, setDraft] = useState(item.text ?? '');
  const [refusal, setRefusal] = useState<string | null>(null);
  useEffect(() => { setDraft(item.text ?? ''); setRefusal(null); }, [item.text]);

  const save = useMutation({
    mutationFn: (change: { text?: string; reviewed?: boolean }) =>
      flows.updateFlowTranslation(flowId, { language, node_id: item.node_id, path: item.path, ...change }),
    onSuccess: (res) => {
      // The piece as saved replaces its row in every cached copy of this language.
      qc.setQueriesData<{ data: flows.FlowTranslations }>({ queryKey: ['flow-translations', flowId] }, (prev) =>
        prev && prev.data.language === language
          ? { ...prev, data: { ...prev.data, items: prev.data.items.map((i) => (i.node_id === res.data.node_id && i.path === res.data.path ? res.data : i)) } }
          : prev);
    }
  });

  const text = draft.trim();
  const dirty = text !== (item.text ?? '').trim();
  const max = item.max_length ?? null;
  const problems = text ? placeholderProblems(item.source_text, text) : { missing: [], extra: [] };
  const localError = max !== null && text.length > max
    ? t('flows.translations.tooLong', { max })
    : problems.missing.length > 0
      ? t('flows.translations.lostParts', { parts: problems.missing.join(', ') })
      : problems.extra.length > 0
        ? t('flows.translations.extraParts', { parts: problems.extra.join(', ') })
        : undefined;

  function saveText() {
    setRefusal(null);
    save.mutate({ text }, {
      onSuccess: () => notifySuccess(t('flows.translations.saved'), t('flows.translations.titleShort')),
      onError: (e) => setRefusal(refusalText(e, t('common.unknownError')))
    });
  }

  function takeBack() {
    openConfirmModal({
      title: t('flows.translations.undo.title'),
      children: <Text role="bodySmall">{t('flows.translations.undo.body', { language: languageName })}</Text>,
      labels: { confirm: t('flows.translations.undo.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => save.mutate({ text: '' }, { onError: (e) => notifyError(refusalText(e, t('common.unknownError')), t('flows.translations.titleShort')) })
    });
  }

  const sourceColour = item.source === 'edited' ? 'blue' : item.source === 'missing' ? 'orange' : 'gray';

  return (
    <div className="rr-translations__row" data-testid={`translation-${item.node_id}-${item.path}`}>
      <Group justify="space-between" align="center" gap="xs" wrap="wrap">
        <Text role="caption" fw={600}>{pathLabel(item.path, t)}</Text>
        <Group gap={4} wrap="wrap">
          <Badge size="xs" variant="light" color={sourceColour}>{t(`flows.translations.source.${item.source}`, item.source)}</Badge>
          {item.stale && <Badge size="xs" variant="light" color="orange">{t('flows.translations.needsLook')}</Badge>}
          {item.reviewed && <Badge size="xs" variant="light" color="green">{t('flows.translations.readBadge')}</Badge>}
        </Group>
      </Group>
      <div className="rr-translations__pair">
        <div className="rr-translations__original">
          <Text role="caption" c={MUTED}>{t('flows.translations.original')}</Text>
          <Text role="bodySmall">{item.source_text}</Text>
        </div>
        <Textarea
          label={t('flows.translations.in', { language: languageName })}
          value={draft}
          placeholder={item.source === 'missing' ? t('flows.translations.missingPlaceholder') : undefined}
          autosize
          minRows={2}
          maxRows={8}
          onChange={(e) => { setDraft(e.currentTarget.value); setRefusal(null); }}
          description={max !== null ? t('flows.translations.counter', { count: text.length, max }) : undefined}
          error={refusal ?? localError}
        />
      </div>
      <Group justify="flex-end" gap="xs" wrap="wrap">
        {item.source === 'edited' && (
          <Button size="xs" variant="subtle" disabled={save.isPending} onClick={takeBack}>{t('flows.translations.undo.action')}</Button>
        )}
        <Button
          size="xs"
          variant="outline"
          disabled={item.source === 'missing' || dirty || save.isPending}
          onClick={() => save.mutate({ reviewed: !item.reviewed }, {
            onError: (e) => notifyError(refusalText(e, t('common.unknownError')), t('flows.translations.titleShort'))
          })}
        >
          {item.reviewed ? t('flows.translations.markUnread') : t('flows.translations.markRead')}
        </Button>
        <Button size="xs" disabled={!dirty || !text || Boolean(localError)} loading={save.isPending && dirty} onClick={saveText}>
          {t('action.save')}
        </Button>
      </Group>
    </div>
  );
}
